import { useQuery } from '@tanstack/vue-query'
import { MaybeRef } from '@vueuse/core'
import { computed, isRef } from 'vue'
import useInvalidateQueries from './useInvalidateQueries'
import { $http } from '@/plugins'
import { type TicketType, type TicketStateType, TicketStateEnum } from '@/types/sword-desk/tickets/ticket.types'

export interface SwordTicketsResponse {
  tickets: TicketType[];
}

export type SwordDeskTicketsFetchOptions = {
  memberAccountUuid: MaybeRef<string | null>;
  enableQuery?: MaybeRef<boolean>;
}

export const useSwordDeskTicketsFetch = ({ memberAccountUuid, enableQuery }: SwordDeskTicketsFetchOptions) => {
  const internalMemberAccountUuid = computed(() => (isRef(memberAccountUuid) ? memberAccountUuid.value : memberAccountUuid) ?? '')
  const internalEnableQuery = computed(() => {
    if (enableQuery === undefined) return true

    return isRef(enableQuery) ? enableQuery.value : enableQuery
  })

  const queryKey = computed(() => ['sword-desk-tickets', internalMemberAccountUuid.value])

  const {
    data: ticketsData,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery<SwordTicketsResponse, Error>({
    queryKey,
    queryFn: async () => {
      if (!internalMemberAccountUuid.value) {
        return { tickets: [] }
      }
      const { data } = await $http('swordDesk/existingTickets', internalMemberAccountUuid.value)

      return data
    },
    enabled: computed(() => !!internalMemberAccountUuid.value && internalEnableQuery.value),
    staleTime: 5 * 60 * 1000,
  })

  const ticketsCounter = computed(() => {
    if (!tickets.value || tickets.value.length === 0) {
      return 0
    }

    const statesToShowInCounter: Partial<TicketStateType>[] = [
      TicketStateEnum.NEW,
      TicketStateEnum.OPEN,
      TicketStateEnum.PENDING,
      TicketStateEnum['ON HOLD'],
    ]

    return tickets.value.filter((ticket) =>
      statesToShowInCounter.includes(ticket.state.toUpperCase() as TicketStateType),
    ).length
  })

  const tickets = computed(() => ticketsData.value?.tickets || [])

  const invalidateTicketsQuery = useInvalidateQueries(queryKey.value)

  return {
    tickets,
    ticketsCounter,
    isLoading,
    isFetching,
    isError,
    refetch,
    invalidateTicketsQuery,
  }
}

export default useSwordDeskTicketsFetch
